import {FormField, Separator, type FormControl} from 'platform/components';
import {Grid, VStack} from 'platform/foundation';

import {useVehicleCatalogue} from '@dms/shared';

import type {VariantCellValues} from '../schemas/variantCellSchema';
import {FormSection, PASSENGER_CAR, t} from '../utils/catalogueUi';
import {getTechnicalDataFit, type EngineFuel} from '../utils/getTechnicalDataFit';
import {labelWithUnit} from '../utils/labelWithUnit';
import {NumberField} from './NumberField';

type TechnicalDataKey = keyof VariantCellValues['technicalData'];
type NumberKey = Exclude<TechnicalDataKey, 'dimensions' | 'emissionClass'>;
type NumberFieldDefinition = {key: NumberKey; label: string; unit: string};

const KM = 'general.metric.km';
const LITRES = 'general.metric.l100km';
const KWH = 'general.metric.kwh100km';
const G_KM = 'general.metric.gkm';
const MM = 'general.metric.mm';
const KG = 'general.metric.kg';

// Range, consumption and CO2 in the order of the used-vehicle form, under its labels.
const CONSUMPTION_FIELDS: NumberFieldDefinition[] = [
  {key: 'electricRange', label: 'entity.vehicle.labels.batteryElectricRange', unit: KM},
  {key: 'electricRangeCity', label: 'entity.vehicle.labels.electricRangeCity', unit: KM},
  {key: 'combinedRange', label: 'entity.vehicle.labels.combinedRange', unit: KM},
  {key: 'combinedRangeCity', label: 'entity.vehicle.labels.combinedRangeCity', unit: KM},
  {
    key: 'fuelConsumptionWltpCombined',
    label: 'entity.vehicle.labels.fuelConsumptionWltpCombined',
    unit: LITRES,
  },
  {
    key: 'fuelConsumptionWltpCityCenter',
    label: 'entity.vehicle.labels.fuelConsumptionWltpCityCenter',
    unit: LITRES,
  },
  {
    key: 'fuelConsumptionWltpOutskirts',
    label: 'entity.vehicle.labels.fuelConsumptionWltpOutskirts',
    unit: LITRES,
  },
  {
    key: 'fuelConsumptionWltpCountryRoads',
    label: 'entity.vehicle.labels.fuelConsumptionWltpCountryRoads',
    unit: LITRES,
  },
  {
    key: 'fuelConsumptionWltpHighway',
    label: 'entity.vehicle.labels.fuelConsumptionWltpHighway',
    unit: LITRES,
  },
  {
    key: 'fuelConsumptionWltpWeighted',
    label: 'entity.vehicle.labels.fuelConsumptionWltpWeighted',
    unit: LITRES,
  },
  {
    key: 'fuelConsumptionWltpCombinedKwh',
    label: 'entity.vehicle.labels.fuelConsumptionWltpCombined',
    unit: KWH,
  },
  {
    key: 'fuelConsumptionWltpCityCenterKwh',
    label: 'entity.vehicle.labels.fuelConsumptionWltpCityCenter',
    unit: KWH,
  },
  {
    key: 'fuelConsumptionWltpOutskirtsKwh',
    label: 'entity.vehicle.labels.fuelConsumptionWltpOutskirts',
    unit: KWH,
  },
  {
    key: 'fuelConsumptionWltpCountryRoadsKwh',
    label: 'entity.vehicle.labels.fuelConsumptionWltpCountryRoads',
    unit: KWH,
  },
  {
    key: 'fuelConsumptionWltpHighwayKwh',
    label: 'entity.vehicle.labels.fuelConsumptionWltpHighway',
    unit: KWH,
  },
  {
    key: 'fuelConsumptionWltpWeightedCombinedKwh',
    label: 'entity.vehicle.labels.fuelConsumptionWltpWeightedCombinedKwh',
    unit: KWH,
  },
  {key: 'carbonDioxideEmission', label: 'entity.vehicle.labels.emissionsCo2WO', unit: G_KM},
  {
    key: 'carbonDioxideEmissionDischarged',
    label: 'entity.vehicle.labels.carbonDioxideEmissionDischarged',
    unit: G_KM,
  },
  {
    key: 'carbonDioxideEmissionWeightedCombined',
    label: 'entity.vehicle.labels.carbonDioxideEmissionWeightedCombined',
    unit: G_KM,
  },
];
const BODY_FIELDS: NumberFieldDefinition[] = [
  {key: 'wheelBase', label: 'entity.vehicle.labels.wheelBase', unit: MM},
  {key: 'operatingWeight', label: 'entity.vehicle.labels.operatingWeight', unit: KG},
  {key: 'trunkSpace', label: 'entity.vehicle.labels.trunkSpace', unit: 'general.metric.l'},
];
const DIMENSIONS = [
  {name: 'technicalData.dimensions.length', label: 'general.labels.length'},
  {name: 'technicalData.dimensions.width', label: 'general.labels.width'},
  {name: 'technicalData.dimensions.height', label: 'general.labels.height'},
] as const;

export function VariantTechnicalDataFields(props: {
  control: FormControl<VariantCellValues>;
  engine: EngineFuel;
}) {
  const [catalogue, {isLoading}] = useVehicleCatalogue(PASSENGER_CAR);
  const keys: readonly TechnicalDataKey[] = getTechnicalDataFit(props.engine).variantKeys;
  const renderNumberField = (field: NumberFieldDefinition) => (
    <NumberField
      key={field.key}
      control={props.control}
      name={`technicalData.${field.key}`}
      label={labelWithUnit(field.label, field.unit)}
    />
  );
  return (
    <VStack spacing={4}>
      <Separator spacing={0} />
      <FormSection title={t('entity.vehicle.labels.consumptionAndEmissionSectionTitle')}>
        <Grid columns={2} spacing={3}>
          {CONSUMPTION_FIELDS.filter((field) => keys.includes(field.key)).map(renderNumberField)}
          <FormField
            control={props.control}
            name="technicalData.emissionClass"
            type="choice"
            menuInPortal
            label={t('entity.vehicle.labels.emissionClass')}
            options={catalogue.emissionClassOptions ?? []}
            isLoading={isLoading}
          />
        </Grid>
      </FormSection>
      <Separator spacing={0} />
      <FormSection title={t('page.advertisement.preview.dimensionsAndWeight')}>
        <Grid columns={2} spacing={3}>
          {DIMENSIONS.map((dimension) => (
            <NumberField
              key={dimension.name}
              control={props.control}
              name={dimension.name}
              label={labelWithUnit(dimension.label, MM)}
            />
          ))}
          {BODY_FIELDS.map(renderNumberField)}
          <NumberField
            control={props.control}
            name="gvwKg"
            label={labelWithUnit('entity.vehicle.labels.maxWeight', KG)}
          />
          <NumberField
            control={props.control}
            name="payloadKg"
            label={labelWithUnit('entity.vehicle.labels.maxLoadCapacity', KG)}
          />
        </Grid>
      </FormSection>
    </VStack>
  );
}
